"use client";

import React, { useCallback, useMemo, useRef } from "react";
import {
  useController,
  useFormContext,
  useWatch,
  type Control,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/components/ui/select";
import { BackendLabel } from "../../backend-label";
import {
  backendSelectContentClass,
  backendSelectItemClass,
  backendSelectTriggerClass,
} from "../../backend-ui";
import { cn } from "@/lib/utils";
import ValidationPopover, {
  type ValidationErrorT,
  type ValidationPopoverBgT,
} from "../../../ui/validation-popover";

/**
 * Select option interface
 */
interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/**
 * Props for ControlledSelect component
 */
interface ControlledSelectProps<
  TFieldValues extends FieldValues = FieldValues,
> {
  // Core Props (konsistent mit Controlled Inputs)
  name: FieldPath<TFieldValues>;
  control?: Control<TFieldValues>;
  onFieldBlur?: (fieldName: string) => void;
  onSelectionChange?: (value: string) => void;

  // Select-spezifische Props
  options: SelectOption[];
  placeholder?: string;
  /** Shown as a non-interactive row when `options` is empty. */
  emptyMessage?: string;
  disabled?: boolean;
  readOnly?: boolean;

  // Label Props (separat, da Shadcn UI)
  label?: string;
  required?: boolean;
  labelClassName?: string;

  // Styling Props
  className?: string;
  triggerClassName?: string;

  // Validation Popover Props
  bgVariant?: ValidationPopoverBgT;
}

/**
 * Controlled Select component with React Hook Form integration
 *
 * @example
 * ```tsx
 * <ControlledSelect
 *   name="condition"
 *   options={VEHICLE_CONDITION_OPTIONS}
 *   label="Fahrzeugzustand"
 *   placeholder="Zustand auswählen"
 * />
 * ```
 */
export function ControlledSelect<
  TFieldValues extends FieldValues = FieldValues,
>({
  name,
  control,
  options,
  placeholder = "Auswählen...",
  emptyMessage = "Keine Optionen gefunden.",
  label,
  required,
  labelClassName,
  disabled,
  readOnly,
  className,
  triggerClassName,
  bgVariant = "accent",
  onFieldBlur,
  onSelectionChange,
}: ControlledSelectProps<TFieldValues>) {
  const formContext = useFormContext<TFieldValues>();
  const actualControl = control || formContext?.control;

  if (!actualControl) {
    throw new Error(
      "ControlledSelect must be used within a FormProvider or with control prop"
    );
  }

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control: actualControl,
  });

  // ✅ Use useWatch for reactive updates after form.reset()
  const watchedValue = useWatch({
    name,
    control: actualControl,
  });

  // Error handling for validation popover (konsistent mit BackendInput)
  const errorMessage = error?.message;
  const hasError = Boolean(errorMessage);
  const errorMessages = useMemo(() => {
    return errorMessage ? [errorMessage] : [];
  }, [errorMessage]);

  const handleValueChange = useCallback(
    (value: string) => {
      // Prevent selection in read-only mode
      if (readOnly) return;
      field.onChange(value);
      onSelectionChange?.(value);
      // Re-validate with the new value so a stale error (e.g. from a previous
      // publish attempt) clears immediately instead of on the next blur.
      onFieldBlur?.(name);
    },
    [field, onSelectionChange, onFieldBlur, name, readOnly]
  );

  const handleClose = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        return;
      }

      // Radix emits onOpenChange(false) BEFORE onValueChange, so defer the
      // blur handling until the selected value is committed to the form.
      // Otherwise validation runs against the previous (often empty) value
      // and marks a freshly selected field as invalid.
      queueMicrotask(() => {
        field.onBlur();
        onFieldBlur?.(name);
      });
    },
    [field, onFieldBlur, name]
  );

  // ✅ Use watched value for reactive updates, fallback to field.value
  const currentValue = watchedValue || field.value;
  const normalizedValue =
    currentValue === null || currentValue === undefined
      ? undefined
      : String(currentValue);

  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const triggerNode = (
    <SelectTrigger
      ref={triggerRef}
      className={cn(
        backendSelectTriggerClass,
        // Force the card background to win over Radix SelectTrigger's bg-background
        // (which references an undefined var in the backend theme → transparent).
        !readOnly && "!bg-[var(--c-card)]",
        readOnly && "!cursor-default !bg-[var(--c-surface-sunken)] select-text",
        "font-normal",
        "!h-auto min-h-[41px] sm:min-h-[41px]",
        error && "border-destructive focus:border-destructive",
        triggerClassName,
        "data-[placeholder]:text-sm data-[placeholder]:font-normal"
      )}
      aria-invalid={hasError || undefined}
      aria-label={
        label
          ? normalizedValue
            ? `${label}: ${options.find((opt) => opt.value === normalizedValue)?.label || normalizedValue}`
            : `${label}${placeholder ? `, ${placeholder}` : ""}`
          : placeholder || undefined
      }
    >
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
  );

  return (
    <div className={cn("flex flex-col gap-1 sm:gap-1.5", className)}>
      {label && (
        <BackendLabel
          htmlFor={name}
          required={required}
          className={labelClassName}
        >
          {label}
        </BackendLabel>
      )}

      <Select
        value={normalizedValue}
        onValueChange={handleValueChange}
        onOpenChange={handleClose}
        disabled={disabled}
      >
        <ValidationPopover
          error={
            hasError && errorMessages.length > 0
              ? (errorMessages as (string | ValidationErrorT)[])
              : undefined
          }
          bgVariant={bgVariant}
          triggerRef={triggerRef}
          triggerOnHover={hasError && errorMessages.length > 0}
        >
          {triggerNode}
        </ValidationPopover>

        <SelectContent className={backendSelectContentClass}>
          {options.length === 0 ? (
            // Radix SelectItem rejects empty values, so the empty state is a
            // plain non-interactive row. Without it the open content is an
            // invisible empty box and the trigger looks broken.
            <div
              role="option"
              aria-selected={false}
              aria-disabled="true"
              className={cn(
                backendSelectItemClass,
                "cursor-default border-t-0 text-[var(--c-muted)] hover:bg-transparent"
              )}
            >
              {emptyMessage}
            </div>
          ) : (
            options.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                disabled={option.disabled || readOnly}
                className={backendSelectItemClass}
              >
                {option.label}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
    </div>
  );
}
